const INTERNAL_CONTEXT_KEY_PATTERN = /(tenant.*(slug|id|database|db)|sentry|graylog|minio|\bs3\b|raw.*evidence|evidence.*(url|link|download|object|bucket|key)|internal.*(url|link)|archive.*(url|link|object|bucket|key)|object.*(key|url)|bucket|download.*url|support.*link|url$)/i;
const INTERNAL_CONTEXT_VALUE_PATTERN = /(tenant-db|tenant-events|crm_tenant_|sentry\.io|graylog|amazonaws\.com|minio|s3:\/\/|https?:\/\/|\/\/|(?:^|[/?#&=])(raw|evidence|internal|archive|download|objectKey|bucket|s3|minio)(?:[/?#&=._-]|$))/i;
const TENANT_SAFE_SUPPORT_LINK_PATTERN = /^\/system\/events\/[A-Za-z0-9._~:@-]+\/?$/;

function pickString(value) {
  return value === undefined || value === null ? '' : String(value);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isInternalContextEntry(key, value) {
  if (INTERNAL_CONTEXT_KEY_PATTERN.test(String(key || ''))) {
    return true;
  }

  return typeof value === 'string' && INTERNAL_CONTEXT_VALUE_PATTERN.test(value);
}

export function sanitizeSystemEventSupportLink(value) {
  const supportLink = pickString(value).trim();
  if (!supportLink || INTERNAL_CONTEXT_VALUE_PATTERN.test(supportLink)) {
    return '';
  }

  return TENANT_SAFE_SUPPORT_LINK_PATTERN.test(supportLink) ? supportLink : '';
}

export function sanitizeSystemEventContext(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeSystemEventContext(item))
      .filter((item) => item !== undefined);
  }

  if (!isPlainObject(value)) {
    if (typeof value === 'string' && INTERNAL_CONTEXT_VALUE_PATTERN.test(value)) {
      return undefined;
    }

    return value;
  }

  return Object.entries(value).reduce((result, [key, item]) => {
    if (isInternalContextEntry(key, item)) {
      return result;
    }

    const sanitizedValue = sanitizeSystemEventContext(item);
    if (sanitizedValue !== undefined) {
      result[key] = sanitizedValue;
    }

    return result;
  }, {});
}

function mapReference(reference) {
  if (!reference || typeof reference !== 'object') {
    return null;
  }

  return {
    type: pickString(reference.type).trim(),
    id: pickString(reference.id).trim(),
    label: pickString(reference.label).trim(),
  };
}

export function mapSystemEventDto(event) {
  const context = sanitizeSystemEventContext(event?.context || null);

  return {
    id: pickString(event?.id).trim(),
    occurredAt: pickString(event?.occurredAt).trim(),
    recordedAt: pickString(event?.recordedAt).trim(),
    severity: pickString(event?.severity || 'INFO').trim(),
    source: pickString(event?.source || 'SYSTEM').trim(),
    eventType: pickString(event?.eventType).trim(),
    regulatoryCategory: pickString(event?.regulatoryCategory || 'OPERATIONAL').trim(),
    retentionClass: pickString(event?.retentionClass || 'OPERATIONAL_180D').trim(),
    title: pickString(event?.title).trim(),
    message: pickString(event?.message).trim(),
    actor: mapReference(event?.actor),
    subject: mapReference(event?.subject),
    requestId: pickString(event?.requestId).trim(),
    correlationId: pickString(event?.correlationId).trim(),
    traceId: pickString(event?.traceId).trim(),
    sessionId: pickString(event?.sessionId).trim(),
    ip: pickString(event?.ip).trim(),
    userAgent: pickString(event?.userAgent).trim(),
    actionLogId: pickString(event?.actionLogId).trim(),
    jobRunId: pickString(event?.jobRunId).trim(),
    fingerprint: pickString(event?.fingerprint).trim(),
    context: context && Object.keys(context).length > 0 ? context : null,
    archived: Boolean(event?.archived),
    archivedAt: pickString(event?.archivedAt).trim(),
    archiveManifestHash: pickString(event?.archiveManifestHash).trim(),
  };
}

export function mapSystemEventsListResponseToDto(response) {
  return {
    events: Array.isArray(response?.events) ? response.events.map(mapSystemEventDto) : [],
    nextCursor: pickString(response?.nextCursor).trim(),
  };
}

function normalizeCountMap(value) {
  if (!isPlainObject(value)) {
    return {};
  }

  return Object.entries(value).reduce((result, [key, count]) => {
    const numeric = Number(count);
    result[key] = Number.isFinite(numeric) ? numeric : 0;
    return result;
  }, {});
}

function mapSummaryWindow(window) {
  return {
    total: Number(window?.total || 0),
    severity: normalizeCountMap(window?.severity),
    source: normalizeCountMap(window?.source),
    category: normalizeCountMap(window?.category),
  };
}

export function mapSystemEventSummaryResponseToDto(response) {
  return {
    last24h: mapSummaryWindow(response?.last24h),
    last7d: mapSummaryWindow(response?.last7d),
  };
}

export function mapSystemEventDetailResponseToDto(response) {
  return mapSystemEventDto(response?.event || response);
}

export function mapSystemEventSupportBundleResponseToDto(response) {
  return {
    supportReference: pickString(response?.supportReference).trim(),
    supportLink: sanitizeSystemEventSupportLink(response?.supportLink),
    eventId: pickString(response?.eventId).trim(),
    tenantSlug: pickString(response?.tenantSlug).trim(),
    occurredAt: pickString(response?.occurredAt).trim(),
    severity: pickString(response?.severity).trim(),
    source: pickString(response?.source).trim(),
    title: pickString(response?.title).trim(),
    requestId: pickString(response?.requestId).trim(),
    correlationId: pickString(response?.correlationId).trim(),
  };
}
